import {
  isMessageBodyKnownReady,
  readMessageBodyCache,
} from "@/lib/cache/messageBodyCache";
import { getFolderSnapshot } from "@/lib/mail/inboxFolderBus";
import type { MailMessageDetail } from "@/lib/types/mail";

/** Corps HTML ou texte non vide. */
export function hasReadableBody(message: MailMessageDetail | null | undefined): boolean {
  if (!message) return false;
  return Boolean(message.bodyHtml?.trim() || message.bodyText?.trim());
}

/**
 * Fetch terminé (texte ou objet seul). Pas un aperçu liste.
 */
export function isFetchedBodyComplete(message: MailMessageDetail | null | undefined): boolean {
  if (!message || message.uid <= 0) return false;
  if (hasReadableBody(message)) return true;
  return message.bodyEmpty === true || message.emlStored === true;
}

/**
 * Corps en **mémoire** (ouverture sync, 0 await IDB).
 */
export function isMessageBodyInMemory(
  accountId: number,
  folder: string,
  uid: number
): boolean {
  if (uid <= 0) return false;
  return isFetchedBodyComplete(readMessageBodyCache(accountId, folder, uid));
}

/**
 * Corps prêt côté client (mémoire **ou** already hydraté / knownReady → IDB).
 * Utile pastilles / expectLocal au clic.
 */
export function isMessageBodyCached(
  accountId: number,
  folder: string,
  uid: number
): boolean {
  if (uid <= 0) return false;
  if (isMessageBodyInMemory(accountId, folder, uid)) return true;
  return isMessageBodyKnownReady(accountId, folder, uid);
}

/** `.eml` déjà présent sur DD02 (flag liste / snapshot). */
export function isServerBodyCached(
  accountId: number,
  folder: string,
  uid: number
): boolean {
  if (uid <= 0) return false;
  const msg = getFolderSnapshot(accountId, folder)?.messages.find((m) => m.uid === uid);
  return Boolean(msg?.bodyCached);
}

/**
 * Prêt à révéler une ligne (son / hold / gate) :
 * corps en mémoire **ou** `.eml` déjà sur DD02 (serveur-first, multi-postes).
 */
export function isClientRevealReady(
  accountId: number,
  folder: string,
  uid: number
): boolean {
  return (
    isMessageBodyInMemory(accountId, folder, uid) ||
    isServerBodyCached(accountId, folder, uid)
  );
}

/**
 * Réception complète côté client : objet/from + corps lisible + méta PJ si annoncées.
 * Si le détail a été évincé du LRU mais déjà known-ready, on valide (PJ déjà vues au prime).
 */
export function isMessageReceptionReady(
  accountId: number,
  folder: string,
  uid: number
): boolean {
  if (uid <= 0) return false;

  const detail = readMessageBodyCache(accountId, folder, uid);
  if (detail && isFetchedBodyComplete(detail)) {
    if (typeof detail.subject !== "string" || typeof detail.from !== "string") {
      return false;
    }
    if (detail.hasAttachments === true && !Array.isArray(detail.attachments)) {
      return false;
    }
    return true;
  }

  return isMessageBodyKnownReady(accountId, folder, uid);
}
